<zag-card class="user-entity-access-panel">
  <div card-header>
    <div class="user-entity-access-panel__header">
      <div>
        <h3 class="zag-card-title">{{ 'entityAccess.panel.title' | translate: { user: userLabel() } }}</h3>
        <p class="zag-card-subtitle">{{ 'entityAccess.panel.subtitle' | translate: { userId: userId() } }}</p>
      </div>
      @if (ready() && canView()) {
        <button
          type="button"
          zagButton="tonal"
          zagType="primary"
          zagSize="sm"
          class="user-entity-access-panel__ghost"
          [zagTooltip]="'entityAccess.panel.addGrant' | translate"
          tooltipPosition="above"
          [disabled]="adding() || isLoading()"
          (click)="onAdd()"
        >
          <span zagIcon="add"></span>
        </button>
      }
    </div>
  </div>

  @if (!ready() || isLoading()) {
    <zag-badge color="neutral">{{ 'entityAccess.panel.loading' | translate }}</zag-badge>
  } @else if (!canView()) {
    <zag-badge color="error" [accent]="true">{{ 'entityAccess.panel.denied' | translate }}</zag-badge>
  } @else if (loadFailed()) {
    <div class="user-entity-access-panel__stack">
      <zag-badge color="error" [accent]="true">{{ 'entityAccess.panel.loadError' | translate }}</zag-badge>
      <button type="button" zagButton zagType="secondary" (click)="onRetry()">
        {{ 'entityAccess.panel.retry' | translate }}
      </button>
    </div>
  } @else {
    @if (alert(); as panelAlert) {
      <div class="user-entity-access-panel__alert" role="alert">
        <zag-badge color="error" [accent]="true">{{ panelAlert.badgeKey | translate }}</zag-badge>
        <p class="user-entity-access-panel__hint">{{ panelAlert.hintKey | translate }}</p>
        <div class="user-entity-access-panel__alert-actions">
          <button type="button" zagButton zagType="secondary" (click)="onDismissAlert()">
            {{ 'entityAccess.panel.dismiss' | translate }}
          </button>
        </div>
      </div>
    }
    @if (adding()) {
      <div class="user-entity-access-panel__picker">
        <p class="user-entity-access-panel__section-title">{{ 'entityAccess.panel.addTitle' | translate }}</p>
        <zag-dropdown
          [options]="entityTypeOptions()"
          [ngModel]="addEntityType()"
          (ngModelChange)="onAddTypeChange($event)"
          [placeholder]="'entityAccess.panel.addTypePlaceholder' | translate"
          [clearable]="true"
        />
        <zag-dropdown
          [options]="entityOptions()"
          [ngModel]="addEntityId()"
          (ngModelChange)="addEntityId.set($event)"
          [placeholder]="'entityAccess.panel.addEntityPlaceholder' | translate"
          [disabled]="addEntityType() === null || entityOptionsLoading()"
          [searchable]="true"
          [searchPlaceholder]="'entityAccess.panel.addSearchPlaceholder' | translate"
          [noResultsText]="'entityAccess.panel.addNoResults' | translate"
          [clearable]="true"
        />
        @if (entityOptionsFailed()) {
          <zag-badge color="error" [accent]="true">{{ 'entityAccess.panel.optionsError' | translate }}</zag-badge>
        }
        <div class="user-entity-access-panel__picker-actions">
          <button type="button" zagButton zagType="secondary" (click)="onAddCancel()">
            {{ 'entityAccess.panel.addCancel' | translate }}
          </button>
          <button type="button" zagButton [disabled]="!canContinueAdd()" (click)="onAddContinue()">
            {{ 'entityAccess.panel.addContinue' | translate }}
          </button>
        </div>
      </div>
    }
    <ng-template #grantRow let-row="row">
      <div class="user-entity-access-panel__group">
        <div class="user-entity-access-panel__group-header">
          <div class="user-entity-access-panel__identity">
            @if (row.label; as label) {
              <zag-avatar [name]="label" size="sm" [variant]="avatarVariant(row)"></zag-avatar>
              <span class="user-entity-access-panel__name">{{ label }}</span>
            } @else {
              <zag-avatar size="sm" variant="error"></zag-avatar>
              <span class="user-entity-access-panel__restricted">
                {{ 'entityAccess.panel.restricted' | translate }}
              </span>
            }
            <zag-badge color="neutral">{{ typeKey(row.grant.entityType) | translate }}</zag-badge>
            @if (row.grant.viaGroup; as via) {
              <zag-badge color="info">{{ 'entityAccess.panel.viaGroup' | translate: { name: via.groupName } }}</zag-badge>
            }
          </div>
          @if (!row.grant.viaGroup) {
            <div class="user-entity-access-panel__actions">
              <button
                type="button"
                zagButton="tonal"
                zagType="secondary"
                zagSize="sm"
                class="user-entity-access-panel__ghost"
                [zagTooltip]="'entityAccess.panel.editGrants' | translate"
                [attr.aria-label]="'entityAccess.panel.editGrants' | translate"
                tooltipPosition="above"
                [disabled]="isDeleting(row)"
                (click)="onEdit(row)"
              >
                <span zagIcon="edit"></span>
              </button>
              <button
                type="button"
                zagButton="tonal"
                zagType="error"
                zagSize="sm"
                class="user-entity-access-panel__ghost"
                [zagTooltip]="'entityAccess.panel.removeGrants' | translate"
                [attr.aria-label]="'entityAccess.panel.removeGrants' | translate"
                tooltipPosition="above"
                [disabled]="isDeleting(row)"
                (click)="onDelete(row)"
              >
                <span zagIcon="delete"></span>
              </button>
            </div>
          }
        </div>
        <div class="user-entity-access-panel__chips">
          @for (permission of row.grant.permissions; track permission) {
            <div class="user-entity-access-panel__chip">
              <span zagIcon="key" class="user-entity-access-panel__chip-icon"></span>
              <span class="user-entity-access-panel__chip-perm">{{ permission }}</span>
            </div>
          }
        </div>
      </div>
    </ng-template>
    @if (directRows().length > 0) {
      <p class="user-entity-access-panel__section-title">{{ 'entityAccess.panel.directGrants' | translate }}</p>
      <div class="user-entity-access-panel__groups">
        @for (row of directRows(); track rowKey(row)) {
          <ng-container [ngTemplateOutlet]="grantRow" [ngTemplateOutletContext]="{ row: row }"></ng-container>
        }
      </div>
    }
    @if (inheritedRows().length > 0) {
      <p class="user-entity-access-panel__section-title">{{ 'entityAccess.panel.inheritedGrants' | translate }}</p>
      <p class="user-entity-access-panel__hint">{{ 'entityAccess.panel.inheritedNotice' | translate }}</p>
      <div class="user-entity-access-panel__groups">
        @for (row of inheritedRows(); track rowKey(row)) {
          <ng-container [ngTemplateOutlet]="grantRow" [ngTemplateOutletContext]="{ row: row }"></ng-container>
        }
      </div>
    }
    @if (allRows().length > 0 && hasRestricted()) {
      <p class="user-entity-access-panel__footnote">{{ 'entityAccess.panel.restrictedHint' | translate }}</p>
    }
    @if (allRows().length === 0 && !adding()) {
      <div class="user-entity-access-panel__empty">
        <zag-badge color="neutral">{{ 'entityAccess.panel.emptyBadge' | translate }}</zag-badge>
        <p class="user-entity-access-panel__hint">{{ 'entityAccess.panel.emptyHint' | translate }}</p>
      </div>
    }
  }
</zag-card>
